/**
 * RecipeManager - Manage recipes and calculate meal quantities
 * Used for bulk meal planning in cafeteria
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChefHat, Plus, Edit2, Trash2, Calculator, Package, Search,
  ClipboardList, CheckCircle, AlertTriangle, X, Save, ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecipeManager = () => {
  const queryClient = useQueryClient();
  const [showRecipeDialog, setShowRecipeDialog] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [calculatorServings, setCalculatorServings] = useState(100);
  const [calculationResult, setCalculationResult] = useState(null);
  
  // Recipe form state
  const [recipeForm, setRecipeForm] = useState({
    name: '',
    description: '',
    meal_type: 'lunch',
    ingredients: [{ name: '', quantity_per_person: 0, unit: 'g', stock_item_id: '' }],
    preparation_steps: [''],
    preparation_time: 30,
    is_global: true,
    serving_notes: '',
    tags: []
  });

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch recipes
  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ['dining-recipes'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/recipes`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch stock items for linking ingredients
  const { data: stockItems = [] } = useQuery({
    queryKey: ['dining-stock-items'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/stock`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create/Update recipe
  const saveRecipeMutation = useMutation({
    mutationFn: async (data) => {
      const url = selectedRecipe 
        ? `${API_URL}/api/admin/dining/recipes/${selectedRecipe.id}`
        : `${API_URL}/api/admin/dining/recipes`;
      const res = await fetch(url, {
        method: selectedRecipe ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to save recipe');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-recipes']);
      setShowRecipeDialog(false);
      resetForm();
      toast.success(selectedRecipe ? 'Recipe updated!' : 'Recipe created!');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete recipe
  const deleteRecipeMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/dining/recipes/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-recipes']);
      toast.success('Recipe deleted!');
    }
  });

  // Calculate quantities
  const calculateMutation = useMutation({
    mutationFn: async ({ recipe_id, num_servings }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/meal-plan/calculate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ recipe_id, num_servings })
      });
      if (!res.ok) throw new Error('Failed to calculate');
      return res.json();
    },
    onSuccess: (data) => {
      setCalculationResult(data);
    },
    onError: (error) => toast.error(error.message)
  });

  // Create requisition
  const requisitionMutation = useMutation({
    mutationFn: async ({ recipe_id, num_servings }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/meal-plan/requisition`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ recipe_id, num_servings, auto_deduct: true })
      });
      if (!res.ok) throw new Error('Failed to create requisition');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['dining-stock']);
      if (data.success) {
        toast.success('Stock requisition completed! Items deducted from stock.');
      } else {
        toast.warning(`Requisition created with ${data.shortages.length} shortage(s)`);
      }
      setShowCalculator(false);
      setCalculationResult(null);
    },
    onError: (error) => toast.error(error.message)
  });

  const resetForm = () => {
    setRecipeForm({
      name: '',
      description: '',
      meal_type: 'lunch',
      ingredients: [{ name: '', quantity_per_person: 0, unit: 'g', stock_item_id: '' }],
      preparation_steps: [''],
      preparation_time: 30,
      is_global: true,
      serving_notes: '',
      tags: []
    });
    setSelectedRecipe(null);
  };

  const openEditDialog = (recipe) => {
    setSelectedRecipe(recipe);
    setRecipeForm({
      name: recipe.name,
      description: recipe.description || '',
      meal_type: recipe.meal_type || 'lunch',
      ingredients: recipe.ingredients || [{ name: '', quantity_per_person: 0, unit: 'g', stock_item_id: '' }],
      preparation_steps: recipe.preparation_steps || [''],
      preparation_time: recipe.preparation_time || 30,
      is_global: recipe.is_global !== false,
      serving_notes: recipe.serving_notes || '',
      tags: recipe.tags || []
    });
    setShowRecipeDialog(true);
  };

  const openCalculator = (recipe) => {
    setSelectedRecipe(recipe);
    setCalculatorServings(100);
    setCalculationResult(null);
    setShowCalculator(true);
  };

  const addIngredient = () => {
    setRecipeForm({
      ...recipeForm,
      ingredients: [...recipeForm.ingredients, { name: '', quantity_per_person: 0, unit: 'g', stock_item_id: '' }]
    });
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...recipeForm.ingredients];
    updated[index] = { ...updated[index], [field]: value };
    setRecipeForm({ ...recipeForm, ingredients: updated });
  };

  const removeIngredient = (index) => {
    if (recipeForm.ingredients.length > 1) {
      setRecipeForm({
        ...recipeForm,
        ingredients: recipeForm.ingredients.filter((_, i) => i !== index)
      });
    }
  };

  const handleSave = () => {
    if (!recipeForm.name) {
      toast.error('Recipe name is required');
      return;
    }
    if (recipeForm.ingredients.some(i => !i.name || i.quantity_per_person <= 0)) {
      toast.error('All ingredients must have a name and quantity');
      return;
    }
    saveRecipeMutation.mutate(recipeForm);
  };

  const filteredRecipes = recipes.filter(r => 
    r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.meal_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const mealTypeColors = {
    breakfast: 'bg-yellow-100 text-yellow-800',
    lunch: 'bg-green-100 text-green-800',
    dinner: 'bg-purple-100 text-purple-800',
    snack: 'bg-blue-100 text-blue-800'
  };

  return (
    <div className="space-y-6" data-testid="recipe-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ChefHat className="h-6 w-6 text-orange-600" />
            Recipe & Meal Planning
          </h2>
          <p className="text-sm text-gray-500">Create recipes and calculate quantities for bulk meals</p>
        </div>
        <Button onClick={() => { resetForm(); setShowRecipeDialog(true); }} data-testid="add-recipe-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Recipe
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search recipes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Recipes Grid */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading recipes...</div>
      ) : filteredRecipes.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <ChefHat className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No recipes yet</p>
            <Button 
              variant="link" 
              onClick={() => { resetForm(); setShowRecipeDialog(true); }}
            >
              Create your first recipe
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRecipes.map((recipe) => (
            <Card key={recipe.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{recipe.name}</CardTitle>
                    <Badge className={mealTypeColors[recipe.meal_type] || 'bg-gray-100'}>
                      {recipe.meal_type}
                    </Badge>
                  </div>
                  <Badge variant={recipe.is_global ? 'secondary' : 'outline'}>
                    {recipe.is_global ? 'Global' : 'Event'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {recipe.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{recipe.description}</p>
                )}
                
                <div className="text-sm text-gray-500 mb-3">
                  <span className="font-medium">{recipe.ingredients?.length || 0}</span> ingredients
                  {' • '}
                  <span className="font-medium">{recipe.preparation_time || 0}</span> min prep
                </div>
                
                {/* Ingredients preview */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {recipe.ingredients?.slice(0, 4).map((ing, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {ing.name}
                    </Badge>
                  ))}
                  {recipe.ingredients?.length > 4 && (
                    <Badge variant="outline" className="text-xs">
                      +{recipe.ingredients.length - 4} more
                    </Badge>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openCalculator(recipe)}
                    data-testid={`calculate-${recipe.id}`}
                  >
                    <Calculator className="h-4 w-4 mr-1" />
                    Calculate
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => openEditDialog(recipe)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteRecipeMutation.mutate(recipe.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Recipe Dialog */}
      <Dialog open={showRecipeDialog} onOpenChange={setShowRecipeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRecipe ? 'Edit Recipe' : 'New Recipe'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Recipe Name *</Label>
                <Input
                  value={recipeForm.name}
                  onChange={(e) => setRecipeForm({ ...recipeForm, name: e.target.value })}
                  placeholder="e.g., Lunch Special"
                />
              </div>
              <div>
                <Label>Meal Type</Label>
                <Select
                  value={recipeForm.meal_type}
                  onValueChange={(v) => setRecipeForm({ ...recipeForm, meal_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakfast">Breakfast</SelectItem>
                    <SelectItem value="lunch">Lunch</SelectItem>
                    <SelectItem value="dinner">Dinner</SelectItem>
                    <SelectItem value="snack">Snack</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={recipeForm.description}
                onChange={(e) => setRecipeForm({ ...recipeForm, description: e.target.value })}
                placeholder="Brief description of the meal..."
                rows={2}
              />
            </div>

            {/* Ingredients */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Ingredients (per person) *</Label>
                <Button size="sm" variant="outline" onClick={addIngredient}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {recipeForm.ingredients.map((ing, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      placeholder="Ingredient name"
                      value={ing.name}
                      onChange={(e) => updateIngredient(idx, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={ing.quantity_per_person || ''}
                      onChange={(e) => updateIngredient(idx, 'quantity_per_person', parseFloat(e.target.value) || 0)}
                      className="w-24"
                    />
                    <Select
                      value={ing.unit}
                      onValueChange={(v) => updateIngredient(idx, 'unit', v)}
                    >
                      <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="g">grams</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="ml">ml</SelectItem>
                        <SelectItem value="liters">liters</SelectItem>
                        <SelectItem value="pieces">pieces</SelectItem>
                        <SelectItem value="cups">cups</SelectItem>
                        <SelectItem value="tbsp">tbsp</SelectItem>
                        <SelectItem value="tsp">tsp</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={ing.stock_item_id || 'none'}
                      onValueChange={(v) => updateIngredient(idx, 'stock_item_id', v === 'none' ? '' : v)}
                    >
                      <SelectTrigger className="w-40"><SelectValue placeholder="Link to stock" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No link</SelectItem>
                        {stockItems.map(item => (
                          <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {recipeForm.ingredients.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => removeIngredient(idx)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preparation Time (minutes)</Label>
                <Input
                  type="number"
                  value={recipeForm.preparation_time}
                  onChange={(e) => setRecipeForm({ ...recipeForm, preparation_time: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Scope</Label>
                <Select
                  value={recipeForm.is_global ? 'global' : 'event'}
                  onValueChange={(v) => setRecipeForm({ ...recipeForm, is_global: v === 'global' })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (Shared)</SelectItem>
                    <SelectItem value="event">Event-specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Serving Notes</Label>
              <Textarea
                value={recipeForm.serving_notes}
                onChange={(e) => setRecipeForm({ ...recipeForm, serving_notes: e.target.value })}
                placeholder="Notes for serving..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecipeDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saveRecipeMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {selectedRecipe ? 'Update' : 'Create'} Recipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calculator Dialog */}
      <Dialog open={showCalculator} onOpenChange={setShowCalculator}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Quantity Calculator
            </DialogTitle>
          </DialogHeader>
          
          {selectedRecipe && (
            <div className="space-y-4 py-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg">{selectedRecipe.name}</h3>
                <p className="text-sm text-gray-600">{selectedRecipe.meal_type} • {selectedRecipe.ingredients?.length} ingredients</p>
              </div>

              <div>
                <Label>Number of Servings (People)</Label>
                <Input
                  type="number"
                  value={calculatorServings}
                  onChange={(e) => setCalculatorServings(parseInt(e.target.value) || 0)}
                  className="text-xl h-14 text-center font-bold"
                  min={1}
                />
              </div>

              <Button 
                className="w-full" 
                onClick={() => calculateMutation.mutate({ recipe_id: selectedRecipe.id, num_servings: calculatorServings })}
                disabled={calculateMutation.isPending}
              >
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Quantities
              </Button>

              {/* Results */}
              {calculationResult && (
                <div className="space-y-4 mt-4">
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      Required Ingredients for {calculationResult.num_servings} servings
                    </h4>
                    
                    <div className="space-y-2">
                      {calculationResult.ingredients?.map((ing, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="font-medium">{ing.name}</span>
                            {ing.stock_item_id && (
                              <span className="ml-2 text-xs text-gray-500">(linked to stock)</span>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold">{ing.quantity_needed.toFixed(2)} {ing.unit}</span>
                            {ing.stock_available !== undefined && (
                              <div className="text-xs">
                                {ing.stock_sufficient ? (
                                  <span className="text-green-600 flex items-center gap-1 justify-end">
                                    <CheckCircle className="h-3 w-3" />
                                    In stock: {ing.stock_available} {ing.unit}
                                  </span>
                                ) : (
                                  <span className="text-red-600 flex items-center gap-1 justify-end">
                                    <AlertTriangle className="h-3 w-3" />
                                    Shortage: {ing.shortage?.toFixed(2)} {ing.unit}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {calculationResult.estimated_cost > 0 && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg flex justify-between items-center">
                        <span className="font-medium">Estimated Cost</span>
                        <span className="text-xl font-bold">${calculationResult.estimated_cost.toFixed(2)}</span>
                      </div>
                    )}

                    {/* Stock status summary */}
                    {calculationResult.stock_status?.length > 0 && (
                      <div className={`mt-4 p-3 rounded-lg ${calculationResult.all_stock_sufficient ? 'bg-green-50' : 'bg-red-50'}`}>
                        {calculationResult.all_stock_sufficient ? (
                          <p className="text-green-700 flex items-center gap-2">
                            <CheckCircle className="h-5 w-5" />
                            All ingredients available in stock!
                          </p>
                        ) : (
                          <p className="text-red-700 flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5" />
                            Some ingredients have insufficient stock
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Requisition Button */}
                  <Button 
                    className="w-full" 
                    onClick={() => requisitionMutation.mutate({ recipe_id: selectedRecipe.id, num_servings: calculatorServings })}
                    disabled={requisitionMutation.isPending}
                    variant={calculationResult.all_stock_sufficient ? 'default' : 'outline'}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Request from Storeroom & Deduct Stock
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecipeManager;
